import {useFormik} from 'formik'
import React, {useState} from 'react'
import {Modal} from 'react-bootstrap'
import Container from 'react-bootstrap/Container'
import {useIntl} from 'react-intl'
import {toast} from 'react-toastify'
import useCancelToken from '../../../../../../_gori/hooks/UseCancelToken'
import {Button, ValidationErrorModal} from '../../../../../../_gori/partials/widgets'
import SettingsService from '../../../core/_requests'

type Props = {
  show: boolean
  handleClose: () => void
  data: {immediate_manifest: boolean}
}

const ImmediateManifestModal: React.FC<Props> = ({show, handleClose, data}) => {
  const intl = useIntl()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [validationErrors, setValidationErrors] = useState<any>()
  const {newCancelToken, isCancel} = useCancelToken()

  const formik = useFormik({
    initialValues: {
      immediate_manifest: data?.immediate_manifest ?? false,
    },
    enableReinitialize: true,
    onSubmit: async (values) => {
      setIsLoading(true)
      try {
        const config = {
          cancelToken: newCancelToken(),
        }
        const payload = {
          immediate_manifest: values.immediate_manifest,
        }
        const {message} = await SettingsService.updateManifest(payload, config)
        if (message) {
          toast.success(intl.formatMessage({id: message}))
          handleCloseModal()
        }
      } catch (error: any) {
        if (isCancel(error)) return
        setValidationErrors(error?.response)
        // Revert toggle to previous state on error (Requirement 2.5)
        formik.setFieldValue('immediate_manifest', data?.immediate_manifest ?? false)
      } finally {
        setIsLoading(false)
      }
    },
  })

  const handleCloseModal = () => {
    formik.resetForm()
    handleClose()
  }

  return (
    <Modal
      id='gori_modal_settings_immediate_manifest'
      tabIndex={-1}
      aria-hidden='true'
      centered
      dialogClassName='modal-size-md h-auto'
      contentClassName='modal-padding'
      show={show}
      backdrop='static'
      onHide={handleCloseModal}
    >
      {validationErrors && (
        <ValidationErrorModal
          handleClose={() => {
            setValidationErrors(undefined)
          }}
          response={validationErrors}
        />
      )}
      <Modal.Header closeButton>
        <Modal.Title bsPrefix={'fw-bolder fs-1'}>
          {intl.formatMessage({id: 'IMMEDIATE_MANIFEST'})}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Container>
          <div className='mt-4'>
            <div
              className='d-flex align-items-start p-4 rounded mb-3 cursor-pointer'
              onClick={() => formik.setFieldValue('immediate_manifest', false)}
            >
              <input
                type='radio'
                className='form-check-input mt-1 me-3'
                checked={!formik.values.immediate_manifest}
                onChange={() => formik.setFieldValue('immediate_manifest', false)}
              />
              <div>
                <div className='fw-bold'>
                  {intl.formatMessage({id: 'DO_NOT_MANIFEST_IMMEDIATELY'})}
                </div>
                <div className='text-muted fs-6'>
                  {intl.formatMessage({id: 'DO_NOT_MANIFEST_IMMEDIATELY_DESC'})}
                </div>
              </div>
            </div>
            <div
              className='d-flex align-items-start p-4 rounded cursor-pointer'
              onClick={() => formik.setFieldValue('immediate_manifest', true)}
            >
              <input
                type='radio'
                className='form-check-input mt-1 me-3'
                checked={formik.values.immediate_manifest}
                onChange={() => formik.setFieldValue('immediate_manifest', true)}
              />
              <div>
                <div className='fw-bold'>{intl.formatMessage({id: 'MANIFEST_IMMEDIATELY'})}</div>
                <div className='text-muted fs-6'>
                  {intl.formatMessage({id: 'MANIFEST_IMMEDIATELY_DESC'})}
                </div>
              </div>
            </div>
          </div>
        </Container>
      </Modal.Body>
      <Modal.Footer>
        <div className='d-flex justify-content-end'>
          <Button
            className='btn btn-secondary me-2'
            label={intl.formatMessage({id: 'CANCEL'})}
            loadingText={intl.formatMessage({id: 'CANCEL'})}
            event={handleCloseModal}
          />
          <Button
            className='btn btn-primary'
            label={intl.formatMessage({id: 'SAVE'})}
            loadingText={intl.formatMessage({id: 'SAVE'})}
            event={formik.submitForm}
            loading={isLoading}
          />
        </div>
      </Modal.Footer>
    </Modal>
  )
}

export {ImmediateManifestModal}
